import { getActiveTenantProfile } from './tenantBranding';

const LEGACY_KEY = 'turboiam-branding';

function tenantDefaults() {
  const profile = getActiveTenantProfile();
  return {
    primaryKey: profile.storageKey,
    companyName: profile.companyName,
    tagline: profile.companyTagline,
    logo: profile.logo,
    logoMark: profile.logoMark,
    logoOnDark: profile.logoOnDark
  };
}

export type StoredBranding = {
  companyName?: string;
  companyTagline?: string;
  logo?: string;
  logoMark?: string;
  logoOnDark?: string;
};

export type ResolvedBranding = {
  companyName: string;
  companyTagline: string;
  logo: string;
  logoMark: string;
  logoOnDark: string;
};

/** Merge localStorage JSON with safe defaults (trimmed name; empty logo falls back to built-in icon). */
export function resolveBrandingFromParsed(data: Record<string, unknown> | null): ResolvedBranding {
  const defaults = tenantDefaults();
  if (!data) {
    return {
      companyName: defaults.companyName,
      companyTagline: defaults.tagline,
      logo: defaults.logo,
      logoMark: defaults.logoMark,
      logoOnDark: defaults.logoOnDark
    };
  }
  const cn = typeof data.companyName === 'string' ? data.companyName.trim() : '';
  const tagline =
    typeof data.companyTagline === 'string' && data.companyTagline.trim()
      ? data.companyTagline.trim()
      : defaults.tagline;
  const logoRaw = typeof data.logo === 'string' ? data.logo.trim() : '';
  const markRaw = typeof data.logoMark === 'string' ? data.logoMark.trim() : '';
  const onDarkRaw = typeof data.logoOnDark === 'string' ? data.logoOnDark.trim() : '';
  const logo = logoRaw || defaults.logo;
  return {
    companyName: cn || defaults.companyName,
    companyTagline: tagline,
    logo,
    logoMark: markRaw || defaults.logoMark || logo,
    logoOnDark: onDarkRaw || defaults.logoOnDark || markRaw || defaults.logoMark || logo
  };
}

export function readBrandingFromStorage(): string | null {
  const { primaryKey } = tenantDefaults();
  return localStorage.getItem(primaryKey) || localStorage.getItem(LEGACY_KEY);
}

export function writeBrandingToStorage(json: string): void {
  const { primaryKey } = tenantDefaults();
  localStorage.setItem(primaryKey, json);
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event('brandingChanged'));
  } catch {
    /* ignore */
  }
}

/** Ensure localStorage branding matches the active tenant (hostname). */
export function ensureTenantBrandingDefaults(): ResolvedBranding {
  const profile = getActiveTenantProfile();
  const { primaryKey } = tenantDefaults();

  // If branding already exists for this tenant, do not overwrite it on boot.
  // This is critical so Settings → Theme & Branding persists across refreshes.
  const existingRaw = localStorage.getItem(primaryKey) || localStorage.getItem(LEGACY_KEY);
  if (existingRaw) {
    try {
      const parsed = JSON.parse(existingRaw) as Record<string, unknown>;
      const resolved = resolveBrandingFromParsed(parsed);

      // If previous versions stored only a single logo field, normalize to full shape.
      // Only write back when we have to fill required fields.
      const needsNormalize =
        !parsed.logoMark ||
        !parsed.logoOnDark ||
        (typeof parsed.logoMark === 'string' && parsed.logoMark.trim().length === 0) ||
        (typeof parsed.logoOnDark === 'string' && parsed.logoOnDark.trim().length === 0);

      if (needsNormalize) {
        writeBrandingToStorage(
          JSON.stringify({
            companyName: resolved.companyName,
            companyTagline: resolved.companyTagline,
            logo: resolved.logo,
            logoMark: resolved.logoMark,
            logoOnDark: resolved.logoOnDark,
          })
        );
      }

      return resolved;
    } catch {
      // Fall through to defaults when storage is corrupted.
    }
  }

  const payload = {
    companyName: profile.companyName,
    companyTagline: profile.companyTagline,
    logo: profile.logo,
    logoMark: profile.logoMark,
    logoOnDark: profile.logoOnDark
  };
  writeBrandingToStorage(JSON.stringify(payload));
  return resolveBrandingFromParsed(payload);
}
